import { ModalService } from "@/components/fpComponents/services/ModalService/ModalService";
import { MessageDataObject } from "./useIframeConnection";
import { useCartModal } from "./useCartModal";
import { usePreloadedIframe } from "./usePreloadedIframe";
import Client from "@/models/Client";
import { ref, computed, nextTick } from "vue";
import { useNewClientModal } from "./useNewClientModal";
import { useBreakpointsApi } from "@/composables/useBreakpoints";
import GoToService from "@/router/GoToService";
import { ProductType } from "@/commons/enums/ProductType";
import { useSimulationStore } from "@/components/calculator/services/SimulationStore";
import { SimulationDemand } from "@/models/Demand";
import { DealCreationMode } from "@/models/Deal";
import { triggerDealsDataRefresh } from "./useClientPreviewDealsData";
import {
  IFRAME_MESSAGE_TYPES,
  type BackToClientFormPayload,
  type GoToClientDetailsPayload,
  type NewDealModalClosePayload,
  type NewDealWithClientCreatedPayload,
  type NewDealWithClientCreationAddNewClientPayload,
  iframeBridge,
} from "./iframeBridgeProtocol";
import { useIframeBridge } from "./useIframeBridge";
import { buildIframeQueryString, clientTypeFromIsCompany } from "./iframeModalQuery";

export const useNewDealWithClientCreationModal = (
  modalService?: ModalService
) => {
  const { isPreloadedIframeAvailable, openModal, retargetModal } = usePreloadedIframe();

  type NewDealWithClientCreationModalParams = {
    goToCart?: boolean;
    anotherClient?: boolean;
    isCompany?: boolean;
    mode?: DealCreationMode;
    productType?: ProductType | null;
    flowTitle?: string;
    userInput?: SimulationDemand;
    simulationId?: string;
    onClientCreationClose?: (response?: Nullable<NewDealModalClosePayload>) => void;
  };

  const clientData = ref<Client | null>(null);
  const goToCartSaved = ref(true);

  const iframeMessageCallback = (
    eventData: MessageDataObject<
      | NewDealWithClientCreatedPayload
      | NewDealWithClientCreationAddNewClientPayload
      | GoToClientDetailsPayload
      | BackToClientFormPayload
    >
  ) => {
    if (
      iframeBridge.toFincrm.newDealWithClientCreation.clientCreated.matches(
        eventData
      ) ||
      iframeBridge.toFincrm.newDealWithClientCreation.anotherClientAdded.matches(
        eventData
      )
    ) {
      clientData.value = eventData?.data?.client || null;
    } else if (
      iframeBridge.toFincrm.newDealWithClientCreation.addNewClient.matches(
        eventData
      )
    ) {
      const { openAddClientModal } = useNewClientModal(modalService);
      openAddClientModal({
        mode: eventData?.data?.mode,
        anotherClient: true,
        clientType: clientTypeFromIsCompany(eventData?.data?.isCompany),
        goToCart: false,
        productType: eventData?.data?.productType ?? null,
        selectExistingClient: true,
      });
    } else if (
      iframeBridge.toFincrm.newDealWithClientCreation.goToClientDetails.matches(
        eventData
      ) &&
      eventData?.data?.client?.id
    ) {
      GoToService.client(eventData.data.client.id);
    } else if (
      iframeBridge.toFincrm.newDealWithClientCreation.backToClientForm.matches(
        eventData
      )
    ) {
      const { openAddClientModal } = useNewClientModal(modalService);
      const { userInput } = useSimulationStore();
      openAddClientModal({
        goToCart: goToCartSaved.value,
        keepClientData: true,
        mode: eventData?.data?.mode,
        userInput: userInput ?? undefined,
        selectExistingClient: true,
        clientType: clientTypeFromIsCompany(eventData?.data?.client?.isCompany),
        // Lets a re-accept continue this still-open wizard instead of
        // closing and reopening it.
        parentModalType: IFRAME_MESSAGE_TYPES.NEW_DEAL_WITH_CLIENT_CREATION_MODAL,
      });
    }
  };

  const getNewDealWithClientCreationModalQuery = (
    params?: NewDealWithClientCreationModalParams,
  ): Record<string, unknown> => {
    const { productType: modeProductType } = useSimulationStore();
    const query: Record<string, unknown> = {
      withClientCreate: 'true',
      goToCart: params?.goToCart ?? true,
      anotherClient: params?.anotherClient ?? false,
      isAnotherClientCompany: params?.isCompany ?? false,
      productType: params?.productType ?? '',
    };
    if (modeProductType) query.modeProductType = modeProductType;
    if (params?.flowTitle) query.flowTitle = params.flowTitle;
    if (params?.mode) query.mode = params.mode;
    if (params?.userInput) query.userInput = JSON.stringify(params.userInput);
    if (params?.simulationId) query.simulationId = params.simulationId;
    return query;
  };

  const finalizeNewDealWithClientCreation = async (
    modalResponse: NewDealModalClosePayload | undefined,
    onClientCreationClose?: NewDealWithClientCreationModalParams["onClientCreationClose"],
  ) => {
    // Before any routing below, so the caller still sees the close on the page that opened the wizard.
    onClientCreationClose?.(modalResponse);
    if (!goToCartSaved.value && clientData.value) {
      return clientData.value;
    }
    if (modalResponse?.mode === "SaveOnDeal" && modalResponse?.clientId) {
      GoToService.client(Number(modalResponse.clientId));
    }
    if (modalResponse?.dealId) {
      GoToService.deal(Number(modalResponse.dealId));
    } else if (
      modalResponse?.clientId &&
      modalResponse?.draftDeals?.[0]?.id &&
      goToCartSaved.value
    ) {
      const { openCartModal } = useCartModal(modalService);
      await openCartModal(
        modalResponse.clientId,
        modalResponse.draftDeals?.[0]?.id
      );
      const { modalClose } = useIframeBridge();
      modalClose.closeNewDealModal(null);
      triggerDealsDataRefresh();
    }
    return modalResponse;
  };

  const openNewDealWithClientCreationModal = async (
    params?: NewDealWithClientCreationModalParams
  ) => {
    goToCartSaved.value = params?.goToCart ?? true;

    if (isPreloadedIframeAvailable.value) {
      const modalResponse = (await openModal(
        IFRAME_MESSAGE_TYPES.NEW_DEAL_WITH_CLIENT_CREATION_MODAL,
        '/deals/newDealModal',
        {},
        {
          persistent: true,
          maxWidth: 962,
          contentClass: isMobile.value ? "" : "delegation-modal-content",
          fullscreen: isMobile.value,
        },
        iframeMessageCallback,
        getNewDealWithClientCreationModalQuery(params),
      )) as NewDealModalClosePayload | undefined;
      return await finalizeNewDealWithClientCreation(modalResponse, params?.onClientCreationClose);
    }

    const { productType: modeProductType } = useSimulationStore();
    const query = buildIframeQueryString(
      {
        goToCart: params?.goToCart ?? true,
        anotherClient: params?.anotherClient ?? false,
        isAnotherClientCompany: params?.isCompany ?? false,
        productType: params?.productType ?? '',
        modeProductType,
        flowTitle: params?.flowTitle,
        mode: params?.mode,
        userInput: params?.userInput,
        simulationId: params?.simulationId,
      },
      { leading: '&' },
    );
    const route = `deals/newDealModal?withClientCreate=true${query}`;
    const modalUrl = GoToService.getLendiV3Url(route, true);
    const modalResponse = (await modalService?.openIframeModal(
      {
        url: modalUrl,
        name: IFRAME_MESSAGE_TYPES.NEW_DEAL_WITH_CLIENT_CREATION_MODAL,
        callback: iframeMessageCallback,
      },
      {
        persistent: true,
        maxWidth: 962,
        contentClass: isMobile.value ? "" : "delegation-modal-content",
        fullscreen: isMobile.value,
      }
    )) as NewDealModalClosePayload | undefined;

    return await finalizeNewDealWithClientCreation(modalResponse, params?.onClientCreationClose);
  };

  /**
   * Continues the already-open deal wizard as the client-creation variant.
   * The created client travels the `createdClient` cookie, so the retarget only
   * hands the superseded awaiter the accepted client and the resume message only
   * carries the parent's goToCart decision.
   */
  const continueInPlace = async (
    params: NewDealWithClientCreationModalParams | undefined,
    client: unknown,
  ) => {
    goToCartSaved.value = params?.goToCart ?? true;
    clientData.value = null;

    const closePromise = retargetModal(
      IFRAME_MESSAGE_TYPES.NEW_DEAL_WITH_CLIENT_CREATION_MODAL,
      iframeMessageCallback,
      client,
    );

    // `send()` picks the target iframe from the rendered `data-active-modal-type`,
    // which Vue only patches on the next tick — sending earlier would route the
    // resume to the legacy iframe and lose it.
    await nextTick();
    const { toLendiCrm } = useIframeBridge();
    toLendiCrm.newDealWithClientCreation.resumeAfterClientCreated({
      goToCart: params?.goToCart ?? true,
    });

    return await finalizeNewDealWithClientCreation(
      (await closePromise) as NewDealModalClosePayload | undefined,
      params?.onClientCreationClose,
    );
  };

  const breakpoints = useBreakpointsApi();
  const isMobile = computed<boolean>(() => breakpoints.smAndDown);

  return {
    iframeMessageCallback,
    openNewDealWithClientCreationModal,
    continueInPlace,
  };
};
